import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { IMAGES, NAV_LINKS } from "@/lib/content";

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "bg-black/90 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={IMAGES.logo} alt="Employee 2 Employer" className="h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`nav-link font-body text-sm tracking-wide text-white/80 hover:text-white transition-colors ${
                pathname === l.to ? "active text-white" : ""
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/checkout"
            data-testid="nav-cta-get-shift"
            data-track="cta_click"
            data-track-label="get_the_shift"
            data-track-location="nav_persistent"
            className="hidden sm:inline-flex items-center bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium text-sm px-6 py-3 transition-colors duration-300"
          >
            Get The Shift
          </Link>
          <button
            data-testid="nav-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10 px-6 py-8" data-testid="mobile-menu">
          <nav className="flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-display text-2xl text-white/90 hover:text-[#D4AF37] transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="font-display text-2xl text-white/90 hover:text-[#D4AF37] transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/checkout"
              data-testid="mobile-nav-get-shift"
              className="mt-2 inline-flex items-center justify-center bg-[#D4AF37] text-black font-body font-medium px-6 py-4"
            >
              Get The Shift
            </Link>
            <Link
              to="/preview"
              data-testid="mobile-nav-read-preview"
              className="inline-flex items-center justify-center border border-white/25 text-white font-body font-medium px-6 py-4"
            >
              Read Chapter One
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Nav;
